import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';
import { api } from '../api';
import { useMe } from '../App';
import { findPerm } from '../perm';
import { DueBadge } from './CampaignDetail';
import { ActivityTypeTag, ActivityTypeFilter, matchesActivityType } from './ActivityType';

export default function MyTasks() {
  const nav = useNavigate();
  const taskPerm = findPerm(useMe().modules, 'my_tasks');
  const [rows, setRows] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [brands, setBrands] = useState([]);
  const [modal, setModal] = useState(false);
  const [toast, setToast] = useState('');

  async function load() { try { setRows(await api('/tasks/mine')); } catch (e) { setToast(e.message); } }
  useEffect(() => { load(); }, []);
  useEffect(() => { api('/brands/options').then(setBrands).catch(() => {}); }, []);
  function flash(m) { setToast(m); setTimeout(() => setToast(''), 2600); }

  async function setStatus(t, status) {
    try { await api(`/tasks/${t.id}/status`, { method: 'PATCH', body: { status } }); load(); }
    catch (e) { flash(e.message); }
  }

  const open = rows ? rows.filter((t) => t.status !== 'done').length : 0;
  const visibleRows = (rows || []).filter(t => matchesActivityType(t, typeFilter));

  return (
    <div>
      <div className="mod-head">
        <div className="titles">
          <div className="page-eyebrow">Marketing</div>
          <h1>My Tasks</h1>
          <div className="count">{rows ? `${open} open · ${rows.length} total` : 'Loading…'}</div>
        </div>
        <button className="btn btn-dark" onClick={() => setModal(true)}><FiPlus /> Add task</button>
      </div>

      <p className="activity-scope-note">Tasks assigned to you or your team. Campaign plans stay in the campaign; their tasks appear here.</p>
      <ActivityTypeFilter value={typeFilter} onChange={setTypeFilter} count={visibleRows.length} total={rows?.length || 0} />
      <div className="table-card">
        {!rows ? <div className="empty">Loading…</div> :
         rows.length === 0 ? <div className="empty"><h3>Nothing assigned yet</h3><p>Add a personal task, or tasks assigned from campaigns will appear here.</p></div> :
         visibleRows.length === 0 ? <div className="empty"><h3>No matching tasks</h3><p>Choose another activity type or clear the filter.</p><button className="btn" onClick={() => setTypeFilter('all')}>Show all types</button></div> :
          <table className="data">
            <thead><tr>
              <th style={{ width: '28%' }}>Task</th><th>Source</th><th>Assigned to</th><th>Brand</th><th>Due</th><th>Status</th><th>Timeline</th>
            </tr></thead>
            <tbody>
              {visibleRows.map((t) => (
                <tr key={t.id} style={{ opacity: t.status === 'done' ? 0.55 : 1 }}>
                  <td style={{ fontWeight: 600 }}>
                    {t.title}
                    <div style={{ marginTop: 5 }}><ActivityTypeTag value={t.activity_type} /></div>
                  </td>
                  <td>
                    {(t.campaign_id || t.parent_campaign_id)
                      ? <button type="button" className="activity-source-link" onClick={() => nav('/m/campaigns/' + (t.campaign_id || t.parent_campaign_id))}>{t.campaign_name || t.parent_campaign_name || ('Campaign #' + (t.campaign_id || t.parent_campaign_id))}</button>
                      : <span className="pill pill-muted">Personal</span>}
                    {t.parent_task_id && <div className="activity-parent-source">Plan: {t.parent_task_title || ('Activity #' + t.parent_task_id)}</div>}
                  </td>
                  <td>{t.assignee_name || 'Unassigned'}</td>
                  <td style={{ color: 'var(--muted)' }}>{t.brand_name || '—'}</td>
                  <td style={{ color: 'var(--muted)' }}>{t.due_date ? t.due_date.slice(0, 10) : '—'}</td>
                  <td>
                    <select className="status-select" disabled={!taskPerm.can_edit} value={t.status} onChange={(e) => setStatus(t, e.target.value)}>
                      <option value="todo">To Do</option>
                      <option value="in_progress">In Progress</option>
                      <option value="approval">Approval</option>
                      <option value="done">Done</option>
                    </select>
                  </td>
                  <td><DueBadge task={t} /></td>
                </tr>
              ))}
            </tbody>
          </table>}
      </div>

      {modal && <PersonalTaskModal brands={brands}
        onClose={() => setModal(false)} onSaved={() => { setModal(false); flash('Task added'); load(); }} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function PersonalTaskModal({ brands, onClose, onSaved }) {
  const [f, setF] = useState({ title: '', description: '', brand_id: '', due_date: '' });
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function save() {
    setError(''); setBusy(true);
    try { await api('/tasks/mine', { method: 'POST', body: { ...f, brand_id: f.brand_id || null } }); onSaved(); }
    catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <h2>Add personal task</h2>
        <div className="field"><label>Task title</label>
          <input value={f.title} autoFocus onChange={set('title')} placeholder="What do you need to do?" /></div>
        <div className="field"><label>Note (optional)</label>
          <input value={f.description} onChange={set('description')} placeholder="Any details" /></div>
        <div className="grid2">
          <div className="field"><label>Brand</label>
            <select value={f.brand_id} onChange={set('brand_id')}>
              <option value="">— None —</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
            </select></div>
          <div className="field"><label>Due date</label>
            <input type="date" value={f.due_date} onChange={set('due_date')} /></div>
        </div>
        {error && <div className="inline-error">{error}</div>}
        <div className="modal-actions">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={save} disabled={busy || !f.title.trim()}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
